import { IssueCaseCategory } from '@gooduncles/gu-app-schema';
import { CellStyle, ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Spin, Tag } from 'antd';
import { format, parseISO } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { issueCaseKindLabel, issueCaseKindTagColor, normalizeCaseKind } from 'src/constants/issue.constants';

import { loadGridState } from 'src/utils/ag-grid-util';
import { CrmUnifiedRowData } from 'src/utils/crm/crm-data-util';
import { createCategoryWithTaskCountRenderer } from 'src/utils/issue/issue-ag-grid-renderers';
import { categoryColorMap } from 'src/utils/issue/issue-ag-grid-util';
import { buildIssuePanelModalData, IssuePanelModalData } from 'src/utils/issue/issue-util';

import { formatDisplayName } from 'src/hooks/subscription/useStaffUsers';
import useUserById from 'src/hooks/swr/useUserById';

import IssueCaseStatusSelector from 'src/atomic-components/atoms/IssueStatusSelector/IssueCaseStatusSelector';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import IssuePanelModal from 'src/components/Issue/IssuePanelModal';

interface CrmBoardTableProps {
  rowData: CrmUnifiedRowData[];
  isLoading: boolean;
}

const TABLE_ID = 'crmBoardTable';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const categoryCellStyle = (params: { data?: CrmUnifiedRowData }): CellStyle | undefined => {
  if (!params.data) return undefined;
  const color = categoryColorMap[params.data.originalData.category as IssueCaseCategory];
  return color ? { color, fontWeight: 500 } : undefined;
};

/**
 * creatorType 이 manager/admin 이면 사용자 정보로 표시명을 만들고, 그 외에는 creatorType 을 그대로 노출
 */
const CreatorCellContent: FC<{ creatorId: string; creatorType: string }> = ({ creatorId, creatorType }) => {
  const { user, userLoading } = useUserById(creatorId);

  if (userLoading) return <Spin size='small' />;

  if (user && (user.role === 'manager' || user.role === 'admin')) {
    return <span>{formatDisplayName(user)}</span>;
  }

  return <span>{creatorType}</span>;
};

const CrmBoardTable: FC<CrmBoardTableProps> = ({ rowData, isLoading }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<CrmUnifiedRowData> | null>(null);

  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [issuePanelData, setIssuePanelData] = useState<IssuePanelModalData>(null);

  const handleOpenDetail = useCallback((row: CrmUnifiedRowData) => {
    const { sourceType, sourceId } = row.originalData;
    setIssuePanelData(buildIssuePanelModalData(row._id, sourceType, sourceId));
    setIssuePanelOpen(true);
  }, []);

  const IssueCategoryRenderer = useMemo(
    () =>
      createCategoryWithTaskCountRenderer<CrmUnifiedRowData>({
        onRowClick: handleOpenDetail,
        showTaskCount: true,
      }),
    [handleOpenDetail],
  );

  const TypeCategorySummaryCellRenderer = useCallback(
    (params: ICellRendererParams<CrmUnifiedRowData>) => {
      const data = params.data;
      if (!data) return null;

      const { category, caseKind } = data.originalData;
      const color = categoryColorMap[category as IssueCaseCategory];
      const kind = normalizeCaseKind(caseKind);

      return (
        <span
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          onClick={() => handleOpenDetail(data)}
        >
          <Tag color={issueCaseKindTagColor[kind]} style={{ margin: 0 }}>
            {issueCaseKindLabel[kind]}
          </Tag>
          <span style={{ color, fontWeight: 500, textDecoration: 'underline' }}>{IssueCategoryRenderer(params)}</span>
        </span>
      );
    },
    [IssueCategoryRenderer, handleOpenDetail],
  );

  const handleCloseIssuePanel = useCallback(() => {
    setIssuePanelOpen(false);
    setIssuePanelData(null);
  }, []);

  const StatusCellRenderer = useCallback((params: ICellRendererParams<CrmUnifiedRowData>) => {
    const data = params.data;
    if (!data) return null;
    return <IssueCaseStatusSelector issueCaseDoc={data.originalData} issueTasks={data.issueTasks} disabled={false} />;
  }, []);

  const CreatorCellRenderer = useCallback((params: ICellRendererParams<CrmUnifiedRowData>) => {
    const data = params.data;
    if (!data) return null;

    const { creatorId, creatorType } = data.originalData;
    const creatorTypeText = String(creatorType ?? '-');

    if (!creatorId) return <span>{creatorTypeText}</span>;
    return <CreatorCellContent creatorId={creatorId} creatorType={creatorTypeText} />;
  }, []);

  const registeredAtFormatter = useCallback((params: ValueFormatterParams<CrmUnifiedRowData>) => {
    if (!params.value) return '-';
    try {
      return format(parseISO(params.value), 'MM.dd HH:mm');
    } catch {
      return params.value;
    }
  }, []);

  const columnDefs: (ColDef<CrmUnifiedRowData> | ColGroupDef<CrmUnifiedRowData>)[] = useMemo(
    () => [
      {
        headerName: '카테고리',
        children: [
          {
            field: 'displayCategory',
            headerName: '요약',
            width: 200,
            pinned: 'left',
            columnGroupShow: 'closed',
            cellRenderer: TypeCategorySummaryCellRenderer,
            filter: 'agSetColumnFilter',
          },
          {
            headerName: '분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            cellStyle: categoryCellStyle,
            filter: 'agSetColumnFilter',
            valueGetter: (params) => params.data?.originalData.category ?? '-',
          },
          {
            field: 'displaySubcategory',
            headerName: '상세분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            filter: 'agSetColumnFilter',
          },
        ],
      },
      {
        headerName: '상태/일시',
        children: [
          {
            field: 'status',
            headerName: '상태',
            width: 150,
            cellRenderer: StatusCellRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: (params: ValueFormatterParams<CrmUnifiedRowData>) => {
                return params.data?.statusText ?? String(params.value);
              },
            },
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 120,
            sort: 'desc',
            valueFormatter: registeredAtFormatter,
          },
        ],
      },
      {
        field: 'storeName',
        headerName: '매장명',
        width: 150,
        filter: true,
        tooltipValueGetter: (params) => `${params.data?.storeName} (${params.data?.storeId})`,
      },
      {
        field: 'privateNote',
        headerName: '내부 메모',
        width: 250,
        cellStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      {
        field: 'linkedMessageCount',
        headerName: '메시지',
        width: 90,
        cellRenderer: (params: ICellRendererParams<CrmUnifiedRowData>) => {
          const count = params.data?.linkedMessageCount ?? 0;
          if (count <= 0) return <span style={{ color: '#bfbfbf' }}>-</span>;
          return <Tag color='geekblue'>메시지 {count}건</Tag>;
        },
      },
      {
        headerName: '생성자',
        width: 140,
        cellRenderer: CreatorCellRenderer,
      },
    ],
    [TypeCategorySummaryCellRenderer, StatusCellRenderer, CreatorCellRenderer, registeredAtFormatter],
  );

  if (isLoading) {
    return (
      <div className='height100 flexColumn flexCenter'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div className='height100 flexColumn'>
      <div className='flexRow flexAlignCenter flexSpaceBetween' style={{ marginBottom: 8 }}>
        <span>총 {rowData.length}건</span>
        <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
      </div>
      <div className='ag-theme-alpine height100'>
        <AgGridReact<CrmUnifiedRowData>
          ref={setGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          rowHeight={36}
          headerHeight={32}
          groupHeaderHeight={32}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          onGridReady={() => loadGridState(gridRef, TABLE_ID)}
        />
      </div>
      {issuePanelData && <IssuePanelModal open={issuePanelOpen} onClose={handleCloseIssuePanel} {...issuePanelData} />}
    </div>
  );
};

export default CrmBoardTable;
